import React, { useState, useEffect, useRef, useMemo } from "react";
import { Formik, Field, Form } from "formik";
import * as Yup from "yup";
import {
    InputGroup,
    FieldWrappers,
    Label,
    SubmitBtn,
    SideCancelBtn,
    Wrapper,
    ScrollableContent,
    ActionSection,
    ModalHeader,
} from "../Styles/InputStyles";
import { MdClose, MdEdit } from "react-icons/md";
import tw from "tailwind-styled-components";
import SelectOptionModal from "../SelectOptionModal";
import { tranfer_payment_method } from "../../Config";
import ConfirmModal from "../Common/ConfirmModal";
import ModalProfileHeader from "../Common/ModalProfileHeader";
import { capitalizeFirstLetter } from "../../helpers";
import VerifyBankAccount from "../Common/VerfiyBankAccount";

const OnlineRefundModal = ({
    initTransferMutate,
    data,
    finalDueRefund,
    initTransferLoader,
    setShowOnlineRefundModal,
    verifyBankAccount,
    verifyAccountLoading,
    isAccountVerified,
    verifiedAccountName,
    setVerifiedAccountName,
    verifyErrorMsg,
    clearVerification,
}) => {
    const [showConfirmTransferModal, setShowConfirmTransferModal] = useState(false);
    const [pendingPayload, setPendingPayload] = useState(null);
    const hasSufficientBalance = Number(data?.payoutBalance || 0) >= Number(data?.refundAmount || 0);
    const initialValues = {
        tenantId: data?.tenantId,
        name: data?.tenantName,
        mobileNo: data?.tenantMobile,
        amount: Math.abs(data?.refundAmount),
        method: "",
        remarks: "",
        // Bank account fields
        accountName: data?.accountDetail?.holderName || "",
        accountNumber: data?.accountDetail?.accountNum || "",
        ifsc: data?.accountDetail?.ifsc || "",
        category: "",
        deductChargesFromVendor: false
    };

    const [height, setHeight] = useState(window.innerHeight);
    const [isMethodModalOpen, setIsMethodModalOpen] = useState(false);
    const [remarksLength, setRemarksLength] = useState(0);

    const [activeTab, setActiveTab] = useState(0);
    const formikRef = useRef(null);

    useEffect(() => {
        const handleResize = () => {
            setHeight(window.innerHeight);
        };

        window.addEventListener("resize", handleResize);
        handleResize();

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const transferMethodOptions = tranfer_payment_method.map((method) => ({
        label: method.label,
        value: method.value,
    }));

    // Yup Validation Schema for Bank Tab
    const bankValidationSchema = Yup.object({
        mobileNo: Yup.string()
            .required("Mobile number is required")
            .matches(/^[0-9]+$/, "Mobile number must contain only numbers")
            .length(10, "Mobile number must be exactly 10 digits"),
        accountName: Yup.string()
            .required("Account holder name is required")
            .matches(
                /^[A-Za-z0-9. ]+$/,
                "Account holder name can only contain letters, numbers, spaces & dots"
            )
            .min(4, "Account holder name must be at least 4 characters long")
            .max(100, "Account holder name cannot exceed 100 characters"),
        accountNumber: Yup.string()
            .required("Account number is required")
            .matches(/^[0-9]{9,18}$/, "Account number must be 9-18 digits"),
        ifsc: Yup.string()
            .required("IFSC code is required")
            .matches(/^[A-Z]{4}0[A-Z0-9]{6}$/, "Invalid IFSC code format"),
        method: Yup.number()
            .required("Please select a transfer method"),
        // category: Yup.string()
        //     .required("Please select category"),
        remarks: Yup.string()
            .max(500, "Remarks cannot exceed 500 characters")
            .nullable()
    });

    const handleSubmit = async (values, { resetForm, setSubmitting }) => {

        let payload = {
            propId: data?.propId,
            tenantId: data?.tenantId,
            name: data?.tenantName,
            mobile: data.tenantMobile,
            accountName: values.accountName,
            accountNumber: values.accountNumber,
            ifsc: values.ifsc,
            amount: Number(values.amount),
            method: values.method,
            remarks: values.remarks || "",
            type: "bank",
            currentBalance: data?.payoutBalance,
            deductChargesFromVendor: values.deductChargesFromVendor ? 1 : 0,
        };

        setPendingPayload(payload);
        setShowConfirmTransferModal(true);
    };

    const getCurrentMethodLabel = (methodValue) => {
        const selectedMethod = tranfer_payment_method.find(
            (method) => method.value === methodValue
        );
        return selectedMethod ? selectedMethod.label : "Select transfer method";
    };

    const handleRemarksChange = (e, setFieldValue) => {
        const value = e.target.value;
        if (value.length <= 500) {
            setFieldValue("remarks", value);
            setRemarksLength(value.length);
        }
    };

    return (
        <Container
            style={{
                height: `${height}px`,
                overflow: "hidden",
                WebkitOverflowScrolling: "touch",
            }}
        >
            <ModalHeader>
                <h1 className="text-white text-lg font-medium">Make Refund</h1>
                <button onClick={() => 
                    {
                        clearVerification ();
                        setShowOnlineRefundModal(false);
                    }
                    }>
                    <MdClose color="white" size={24} />
                </button>
            </ModalHeader>
            <ModalProfileHeader
                title="Due Details"
                name={capitalizeFirstLetter(data?.tenantName)}
                subtitle={`#${data?.propAddress}`}
                amountLabel={"Refund Amount"}
                amount={data?.refundAmount}
                image={data?.selfi}
                onClose={() => setShowOnlineRefundModal(false)}
            />
            <div className="flex items-center justify-between bg-primary-color/10 px-4 py-2">
                <div className="flex items-center gap-2">
                    {/* <MdAccountBalanceWallet className="text-blue-600" size={18} /> */}

                    <span className="text-sm font-medium text-primary-color">
                        Available Balance
                    </span>
                </div>

                <span className="text-lg font-semibold text-primary-color">
                    ₹{Number(data?.payoutBalance || 0).toLocaleString("en-IN")}
                </span>
            </div>
            {!hasSufficientBalance && (
                <div className="flex items-start gap-2 border-t border-red-100 bg-red-50 px-4 py-2">
                    <svg
                        className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                    >
                        <path
                            fillRule="evenodd"
                            d="M18 10A8 8 0 1110 2a8 8 0 018 8zM9 6a1 1 0 112 0v5a1 1 0 11-2 0V6zm1 9a1.25 1.25 0 100-2.5A1.25 1.25 0 0010 15z"
                            clipRule="evenodd"
                        />
                    </svg>

                    <p className="text-xs text-red-700">
                        <span className="font-semibold">Insufficient payout balance.</span>{" "}
                        Please add funds to your payout wallet or choose{" "}
                        <span className="font-semibold">Offline Refund</span>.
                    </p>
                </div>
            )}
            <div className="custom-scrollbar px-6 flex-1 flex flex-col min-h-0">
                <Formik
                    innerRef={formikRef}
                    initialValues={initialValues}
                    validationSchema={bankValidationSchema}
                    onSubmit={handleSubmit}
                    validateOnChange={true}
                    validateOnBlur={true}
                >
                    {({ values, setFieldValue, errors, touched, isSubmitting, handleChange, handleBlur }) => {
                        const handleMethodSelect = (selectedValue) => {
                            setFieldValue("method", selectedValue);
                        };

                        return (
                            <Form className="flex flex-col h-full pb-8"> 
                                <ScrollableContent>
                                    <Wrapper>

                                        <>
                                            {/* <InputGroup>
                                                <Label htmlFor="mobileNo">Mobile Number *</Label>
                                                <FieldWrappers className={errors.mobileNo && touched.mobileNo ? "border-red-500" : ""}>
                                                    <Field
                                                        type="text"
                                                        name="mobileNo"
                                                        id="mobileNo"
                                                        autoComplete="off"
                                                        maxLength="10"
                                                        placeholder="Enter mobile number"
                                                    />
                                                </FieldWrappers>
                                                {errors.mobileNo && touched.mobileNo && (
                                                    <ErrorText>{errors.mobileNo}</ErrorText>
                                                )}

                                            </InputGroup> */}
                                            <InputGroup>
                                                <Label htmlFor="accountName">Account Holder Name *</Label>
                                                <FieldWrappers>
                                                    <Field
                                                        type="text"
                                                        name="accountName"
                                                        id="accountName"
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        value={values.accountName}
                                                        placeholder="Enter account name"
                                                        className={errors.accountName && touched.accountName ? "border-red-500" : ""}
                                                    />
                                                    {isAccountVerified &&
                                                        values.accountName?.trim().toLowerCase() ===
                                                        verifiedAccountName?.trim().toLowerCase() && (
                                                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-green-600 text-xs font-medium">
                                                                ✓ Verified
                                                            </span>
                                                        )}
                                                </FieldWrappers>
                                                {errors.accountName && touched.accountName && (
                                                    <ErrorText>{errors.accountName}</ErrorText>
                                                )}
                                                {isAccountVerified &&
                                                    values.accountName?.trim().toLowerCase() !==
                                                    verifiedAccountName?.trim().toLowerCase() && (
                                                        <div className="mt-2 rounded-lg border border-yellow-200 bg-yellow-50 p-3">
                                                            <div className="flex items-center justify-between">
                                                                <div>
                                                                    <p className="text-sm font-medium text-yellow-800">
                                                                        Name Mismatch
                                                                    </p>

                                                                    <p className="text-xs text-yellow-700 mt-1">
                                                                        Bank account holder name:{" "}
                                                                        <span className="font-semibold">
                                                                            {verifiedAccountName}
                                                                        </span>
                                                                    </p>
                                                                </div>

                                                                <button
                                                                    type="button"
                                                                    className="text-xs font-semibold text-cblue-default hover:underline"
                                                                    onClick={() => {
                                                                        setFieldValue(
                                                                            "accountName",
                                                                            verifiedAccountName,
                                                                        );
                                                                        //setVerifiedAccountName ('');
                                                                    }
                                                                    }
                                                                >
                                                                    Use Verified Name
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}

                                            </InputGroup>

                                            <InputGroup>
                                                <Label htmlFor="accountNumber">Account Number *</Label>
                                                <FieldWrappers>
                                                    <Field
                                                        type="text"
                                                        name="accountNumber"
                                                        id="accountNumber"
                                                        // onChange={handleChange}
                                                        onChange={(e) => {
                                                            setFieldValue(
                                                                "accountNumber",
                                                                e.target.value.replace(/\s/g, "")
                                                            );
                                                            clearVerification();
                                                        }}
                                                        onBlur={handleBlur}
                                                        value={values.accountNumber}
                                                        placeholder="Enter account number"
                                                        className={errors.accountNumber && touched.accountNumber ? "border-red-500" : ""}
                                                    />
                                                </FieldWrappers>
                                                {errors.accountNumber && touched.accountNumber && (
                                                    <ErrorText>{errors.accountNumber}</ErrorText>
                                                )}
                                            </InputGroup>

                                            <InputGroup>
                                                <Label htmlFor="ifsc">IFSC *</Label>
                                                <FieldWrappers>
                                                    <Field
                                                        type="text"
                                                        name="ifsc"
                                                        id="ifsc"
                                                        onChange={(e) => {
                                                            setFieldValue("ifsc", e.target.value.toUpperCase());
                                                        }}
                                                        onBlur={handleBlur}
                                                        value={values.ifsc}
                                                        placeholder="Enter IFSC code"
                                                        className={errors.ifsc && touched.ifsc ? "border-red-500" : ""}
                                                    />
                                                </FieldWrappers>
                                                {errors.ifsc && touched.ifsc && (
                                                    <ErrorText>{errors.ifsc}</ErrorText>
                                                )}
                                                <VerifyBankAccount
                                                    loading={verifyAccountLoading}
                                                    verified={isAccountVerified}
                                                    error={verifyErrorMsg}
                                                    disabled={!values.accountNumber || !values.ifsc}
                                                    onVerify={() =>
                                                        verifyBankAccount({
                                                            accountNumber: values.accountNumber,
                                                            ifsc: values.ifsc,
                                                        })
                                                    }
                                                />
                                            </InputGroup>
                                        </>

                                        {/* Common Fields for both tabs */}
                                        <InputGroup>
                                            <Label htmlFor="method">Transfer Method *</Label>
                                            <FieldWrappers onClick={() => setIsMethodModalOpen(true)}>
                                                <Field
                                                    type="text"
                                                    name="methodDisplay"
                                                    id="methodDisplay"
                                                    autoComplete="off"
                                                    readOnly
                                                    value={getCurrentMethodLabel(values.method)}
                                                    className="w-full border-none outline-none bg-transparent cursor-pointer"
                                                    placeholder="Select transfer method"
                                                />
                                                <MdEdit className="w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors duration-200 flex-shrink-0 mr-2" />
                                            </FieldWrappers>
                                            {errors.method && touched.method && (
                                                <ErrorText>{errors.method}</ErrorText>
                                            )}
                                        </InputGroup>
                                        {/* <div
                                            className={`rounded-lg px-4 py-3 border ${Number(availableAmount) < 100 ? "bg-red-50 border-red-100" : "bg-green-50 border-green-100"}`}
                                        >
                                            <div className="flex items-center justify-between">
                                                <p className={`text-sm font-medium ${availableAmount < 100 ? "text-red-700" : "text-green-700"}`}>
                                                    Available Balance
                                                </p>

                                                <p className={`text-lg font-semibold ${availableAmount < 100 ? "text-red-800" : "text-green-800"}`}>
                                                    ₹{Number(availableAmount || 0).toLocaleString("en-IN")}
                                                </p>
                                            </div>

                                            <p className="text-xs text-green-600 mt-1">
                                                Maximum transfer limit is {Number(availableAmount) > 500000 ? '₹5,00,000' : Number(availableAmount)}
                                            </p>

                                            {values.method === 3 && (
                                                <p className="text-xs text-orange-600 mt-1">
                                                    RTGS minimum amount is ₹2,00,000
                                                </p>
                                            )}
                                        </div> */}
                                        {/* <InputGroup>
                                            <div className="flex justify-between items-center mb-1">
                                                <Label htmlFor="amount">Amount *</Label>

                                                <span className="text-xs font-medium text-green-600">
                                                    Balance ₹{Number(availableAmount).toLocaleString("en-IN")}
                                                </span>
                                            </div>
                                            <FieldWrappers>
                                                <RupeeIcon />
                                                <Field
                                                    type="text"
                                                    name="amount"
                                                    id="amount"
                                                    autoComplete="off"
                                                    className={`pl-6 ${errors.amount && touched.amount ? "border-red-500" : ""}`}
                                                    placeholder="Enter amount"
                                                    min="1"
                                                    max="1000000"
                                                    step="1"
                                                />
                                            </FieldWrappers>
                                            {errors.amount && touched.amount && (
                                                <ErrorText>{errors.amount}</ErrorText>
                                            )}
                                        </InputGroup> */}

                                        <div className="flex items-start justify-between rounded-lg border border-gray-200 px-4 py-3">
                                            <div className="pr-4">
                                                <p className="text-sm font-medium text-gray-500">
                                                    Deduct Transaction Charges from Recipient
                                                </p>

                                                {/* <p className="mt-1 text-xs text-gray-500">
                                                    Vendor will receive amount after deducting transfer charges and taxes.
                                                </p> */}
                                            </div>

                                            <Field
                                                type="checkbox"
                                                name="deductChargesFromVendor"
                                                checked={values.deductChargesFromVendor}
                                                onChange={(e) =>
                                                    setFieldValue(
                                                        "deductChargesFromVendor",
                                                        e.target.checked
                                                    )
                                                }
                                                className="h-5 w-5 accent-primary-color"
                                            />
                                        </div>

                                        <InputGroup>
                                            <Label htmlFor="remarks">Remarks</Label>
                                            <Textarea
                                                id="remarks"
                                                name="remarks"
                                                placeholder="Add any additional notes here..."
                                                value={values.remarks}
                                                onChange={(e) => handleRemarksChange(e, setFieldValue)}
                                                onBlur={handleBlur}
                                                maxLength={500}
                                            />
                                            <CharacterCount className={values.remarks?.length > 450 ? "text-orange-500" : "text-gray-400"}>
                                                {values.remarks?.length || 0}/500
                                            </CharacterCount>
                                            {errors.remarks && touched.remarks && (
                                                <ErrorText>{errors.remarks}</ErrorText>
                                            )}
                                        </InputGroup>
                                    </Wrapper>
                                </ScrollableContent>

                                <ActionSection className="shrink-0">
                                    <SideCancelBtn type="button" onClick={() => setShowOnlineRefundModal(false)}>
                                        Cancel
                                    </SideCancelBtn>
                                    <SubmitBtn type="submit" disabled={isSubmitting || initTransferLoader}>
                                        {isSubmitting || initTransferLoader ? "Submitting..." : "Submit"}
                                    </SubmitBtn>
                                </ActionSection>

                                {/* Transfer Method Selection Modal */}
                                <SelectOptionModal
                                    isOpen={isMethodModalOpen}
                                    onClose={() => setIsMethodModalOpen(false)}
                                    title="Select Transfer Method"
                                    options={transferMethodOptions}
                                    selectedValue={values.method}
                                    onSelect={handleMethodSelect}
                                    searchPlaceholder="Search transfer methods..."
                                />
                            </Form>
                        );
                    }}

                </Formik>
                <ConfirmModal
                    isOpen={showConfirmTransferModal}
                    setOpenModel={setShowConfirmTransferModal}
                    title="I Confirm"
                    message={
                        pendingPayload?.deductChargesFromVendor
                            ? `You are about to transfer ₹${Number(
                                pendingPayload?.amount || 0
                            ).toLocaleString("en-IN")}. Transfer charges will be deducted from the vendor. Do you want to continue?`
                            : `You are about to transfer ₹${Number(
                                pendingPayload?.amount || 0
                            ).toLocaleString("en-IN")}. Do you want to continue?`
                    }
                    onClose={() => {
                        setShowConfirmTransferModal(false);
                        setPendingPayload(null);
                    }}
                    onConfirm={() => {
                        if (hasSufficientBalance)
                            initTransferMutate(pendingPayload);
                    }}
                    loading={initTransferLoader}
                    confirmBtnText="I Confirm"
                    confirmBtnClassName="bg-primary-color text-white border border-primary-color hover:opacity-90"
                />
            </div>
        </Container>
    );
};

const Container = tw.div`bg-white w-full h-full flex flex-col relative`;
const Textarea = tw.textarea`min-h-10 max-h-40 border border-gray-300 rounded-md pl-2 py-2 focus:ring-1 focus:ring-sky-600 focus:border-sky-600 focus:outline-none duration-200 w-full h-20 resize-y break-words overflow-hidden`;
const ErrorText = tw.div`text-red-500 text-sm ml-0.5`;
const CharacterCount = tw.div`text-xs mt-1 text-right`;
const Text = tw.p`text-gray-500 text-xs font-normal `;

const SimpleInput = tw.input`
    w-full 
    border 
    border-gray-300 
    rounded-md 
    px-3 
    py-2 
    focus:ring-1 
    focus:ring-sky-600 
    focus:border-sky-600 
    focus:outline-none 
    duration-200
`;

export default OnlineRefundModal;